import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "keepsoul_cookie_consent";
const CONSENT_VERSION = "v1.0-2026-05-25";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj?.version !== CONSENT_VERSION) return null;
    return obj;
  } catch {
    return null;
  }
}

function writeConsent(choice) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...choice,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      })
    );
  } catch {
    /* ignore */
  }
  // Notify any subscriber (analytics loaders) — none today, kept for future
  window.dispatchEvent(
    new CustomEvent("keepsoul:cookie-consent-updated", { detail: choice })
  );
}

export default function CookieBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true, // always on, non-editable
    analytics: false,
  });

  // Decide visibility on mount and listen for re-open requests from /legal/cookies
  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const reopen = () => {
      const c = readConsent();
      if (c) setPrefs({ necessary: true, analytics: !!c.analytics });
      setShowConfig(true);
      setVisible(true);
    };
    window.addEventListener("keepsoul:cookie-settings-open", reopen);
    return () =>
      window.removeEventListener("keepsoul:cookie-settings-open", reopen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true });
    setVisible(false);
    setShowConfig(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false });
    setVisible(false);
    setShowConfig(false);
  };
  const savePrefs = () => {
    writeConsent({ necessary: true, analytics: !!prefs.analytics });
    setVisible(false);
    setShowConfig(false);
  };

  return (
    <>
      {/* Bottom banner */}
      {!showConfig && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6 pb-safe-or-3"
          role="dialog"
          aria-live="polite"
          aria-label={t("cookie_banner_title")}
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto rounded-lg border border-gold-subtle bg-obsidian-2/95 backdrop-blur shadow-2xl p-4 sm:p-5">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <Cookie
                className="w-6 h-6 text-gold flex-shrink-0 hidden sm:block"
                strokeWidth={1.4}
              />
              <div className="flex-1 text-sm text-gold-light">
                <h3 className="font-display text-lg text-gold mb-1">
                  {t("cookie_banner_title")}
                </h3>
                <p className="text-faded text-xs sm:text-sm leading-relaxed">
                  {t("cookie_banner_msg")}{" "}
                  <Link
                    to="/legal/cookies"
                    className="text-gold-light underline hover:text-gold"
                    data-testid="cookie-banner-policy-link"
                  >
                    {t("cookie_banner_more")}
                  </Link>
                </p>
              </div>
              <div className="grid grid-cols-1 sm:flex sm:flex-row gap-2 md:flex-col md:gap-2 md:min-w-[170px]">
                <button
                  type="button"
                  className="btn-gold px-4 py-2 text-sm justify-center"
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                >
                  {t("cookie_accept_all")}
                </button>
                <button
                  type="button"
                  className="btn-ghost px-4 py-2 text-sm justify-center"
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                >
                  {t("cookie_reject_all")}
                </button>
                <button
                  type="button"
                  className="text-xs text-faded hover:text-gold-light underline underline-offset-2 text-center"
                  onClick={() => setShowConfig(true)}
                  data-testid="cookie-configure"
                >
                  {t("cookie_configure")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Config modal */}
      {showConfig && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-3 sm:px-6 py-3 sm:py-8"
          role="dialog"
          aria-modal="true"
          aria-label={t("cookie_modal_title")}
          data-testid="cookie-config-modal"
        >
          <div className="max-w-2xl w-full rounded-lg border border-gold-subtle bg-obsidian-2/95 backdrop-blur shadow-2xl p-5 sm:p-7 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-display text-2xl text-gold">
                {t("cookie_modal_title")}
              </h3>
              <button
                type="button"
                onClick={() => setShowConfig(false)}
                className="text-faded hover:text-gold p-1"
                aria-label="close"
                data-testid="cookie-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs sm:text-sm text-faded mb-5 leading-relaxed">
              {t("cookie_modal_intro")}{" "}
              <Link
                to="/legal/cookies"
                className="text-gold-light underline hover:text-gold"
              >
                {t("cookie_banner_more")}
              </Link>
            </p>

            <div className="space-y-3">
              {/* Necessary */}
              <div className="rounded border border-gold-subtle bg-obsidian/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-label text-gold-light mb-1">
                      {t("cookie_cat_necessary")}
                    </h4>
                    <p className="text-xs text-faded">
                      {t("cookie_cat_necessary_desc")}
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-not-allowed opacity-60">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="accent-gold w-4 h-4"
                      data-testid="cookie-cat-necessary"
                    />
                    <span className="ml-2 text-xs text-faded">
                      {t("cookie_always_on")}
                    </span>
                  </label>
                </div>
              </div>

              {/* Analytics */}
              <div className="rounded border border-gold-subtle bg-obsidian/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-label text-gold-light mb-1">
                      {t("cookie_cat_analytics")}
                    </h4>
                    <p className="text-xs text-faded">
                      {t("cookie_cat_analytics_desc")}
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prefs.analytics}
                      onChange={(e) =>
                        setPrefs((p) => ({
                          ...p,
                          analytics: e.target.checked,
                        }))
                      }
                      className="accent-gold w-4 h-4"
                      data-testid="cookie-cat-analytics"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                className="btn-ghost px-4 py-2 text-sm justify-center"
                onClick={rejectAll}
                data-testid="cookie-modal-reject"
              >
                {t("cookie_reject_all")}
              </button>
              <button
                type="button"
                className="btn-gold px-4 py-2 text-sm justify-center"
                onClick={savePrefs}
                data-testid="cookie-modal-save"
              >
                {t("cookie_save_prefs")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
